import { useEffect, useMemo } from 'react';

import { useEcomStore } from '@/data';
import { formatWeightNumber, getCartItemsSubtotal } from '@/utils';
import { useIsServerSide } from '@/hooks';
import { initialEcomState } from '@/data';

import useProducts from './useProducts';

function useSummary() {
  const [data, setEcomState] = useEcomStore();
  const { isServerSide } = useIsServerSide();
  const ecomState = isServerSide ? initialEcomState : data;
  const { products } = useProducts();
  const gramsLimit = parseInt(process.env.NEXT_PUBLIC_GR_LIMIT);

  const preDiscountSubtotal = products.reduce((sum, p) => {
    const v = p?.weightVariant;
    return sum + (v?.price || 0) * p?.quantity;
  }, 0);

  const totalDiscounts = ecomState?.total_discounts || 0;

  const clientSubtotal = useMemo(
    () => getCartItemsSubtotal(products),
    [products],
  );

  const serverSubtotal = ecomState.subtotal_price;

  const subtotal = useMemo(() => {
    if (!products.length) {
      return 0;
    }

    const postDiscountSubtotal = preDiscountSubtotal - totalDiscounts;

    if (!postDiscountSubtotal || postDiscountSubtotal === 0) {
      return clientSubtotal;
    }

    return Math.min(clientSubtotal, postDiscountSubtotal);
  }, [products, clientSubtotal, ecomState.subtotal_price]);

  const totalGrams = useMemo(
    () =>
      products.reduce(
        (result, { weightVariant, quantity }) =>
          result + formatWeightNumber(weightVariant) * quantity,
        0,
      ),
    [products],
  );

  useEffect(() => {
    if (!products.length) {
      setEcomState({
        total_tax: 0,
        total_price: 0,
        subtotal_price: 0,
        total_discounts: 0,
      });
    }
  }, [products.length]);

  return {
    delivery: ecomState.total_delivery,
    discounts: totalDiscounts,
    gramsLimit,
    moreAviability: serverSubtotal <= gramsLimit,
    subtotal,
    preDiscountSubtotal,
    serverSubtotal,
    tax: ecomState.total_tax,
    total: ecomState.total_price,
    totalGrams,
  };
}

export default useSummary;
